import React, { useEffect, useState } from "react";
import { Calendar, MessageCircle, Video, Lock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../api";
import { useAuth } from "../AuthContext";
import { MemberCard } from "./TheBoard";

const DOCTOR_SECTION_ORDER = ["Medical Advisory Board", "Wellness Experts", "Leadership Team"];

const SPECIALTIES = [
    "Hormonal Balance & PCOS",
    "Menstrual Health & Cramps",
    "Hair Loss & Scalp Health",
    "Menopause & Perimenopause",
    "Skin & Inflammation",
    "Nutrition & Supplements",
];

export default function ConsultDoctor() {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [form, setForm] = useState({ name: "", email: "", phone: "", concern: SPECIALTIES[0], note: "" });
    const [sent, setSent] = useState(false);
    const [err, setErr] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [docs, setDocs] = useState([]);
    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    useEffect(() => {
        if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }, [user]);

    useEffect(() => {
        api.get("/doctors")
            .then((r) => setDocs((r.data || []).filter((d) => (d.section || "Medical Advisory Board") !== "Our Core Team")))
            .catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSubmitting(true);
        try {
            await api.post("/consultations", form);
            setSent(true);
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14" data-testid="consult-page">
            <div className="text-center mb-14">
                <p className="eyebrow">Care, on your schedule</p>
                <h1 className="h-display text-[44px] sm:text-[64px] mt-4">Consult a Doctor</h1>
                <p className="text-[var(--body)] max-w-xl mx-auto mt-4">
                    Speak privately with our board-reviewed specialists. 30-minute video consultations, by appointment.
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <aside className="lg:col-span-5 space-y-4">
                    {[
                        { i: <Video size={18} />, t: "30-min video consultation", d: "Confidential, expert-led, results-focused." },
                        { i: <MessageCircle size={18} />, t: "Pre-consult intake", d: "We share a brief questionnaire so the doctor is prepared." },
                        { i: <Calendar size={18} />, t: "Flexible scheduling", d: "Slots Mon–Sat. Reschedule up to 12h before." },
                    ].map((x) => (
                        <div key={x.t} className="card p-5 flex gap-4">
                            <div className="w-10 h-10 rounded-full bg-[var(--bg-alt)] grid place-items-center text-[var(--taupe)] shrink-0">{x.i}</div>
                            <div>
                                <div className="font-serif text-[18px] text-[var(--ink)]">{x.t}</div>
                                <p className="text-[13px] text-[var(--body)] mt-1">{x.d}</p>
                            </div>
                        </div>
                    ))}
                    <div className="card p-5">
                        <div className="overline mb-3">Specialties</div>
                        <ul className="space-y-2 text-[13.5px] text-[var(--ink-soft)]">
                            {SPECIALTIES.map((s) => <li key={s}>· {s}</li>)}
                        </ul>
                    </div>
                </aside>

                <div className="lg:col-span-7 card p-8 lg:p-10">
                    {authLoading ? (
                        <div className="py-16 text-center text-[var(--body)]">Loading…</div>
                    ) : !user ? (
                        <div className="text-center py-14" data-testid="consult-login-gate">
                            <div className="w-14 h-14 rounded-full bg-[var(--bg-alt)] grid place-items-center text-[var(--taupe)] mx-auto mb-5">
                                <Lock size={22} />
                            </div>
                            <h2 className="font-serif text-[26px] text-[var(--ink)] mb-2">Please sign up or log in to book an appointment</h2>
                            <p className="text-[var(--body)] text-[14px] max-w-sm mx-auto mb-7">
                                Appointments are linked to your account so you can view, reschedule and chat about them anytime.
                            </p>
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate("/login", { state: { next: "/consult-doctor" } })}
                                data-testid="consult-login-btn"
                            >
                                Log in / Sign up
                            </button>
                        </div>
                    ) : sent ? (
                        <div className="text-center py-16" data-testid="consult-success">
                            <div className="text-5xl mb-3">🌸</div>
                            <h3 className="font-serif text-[28px] text-[var(--ink)] mb-2">Request received</h3>
                            <p className="text-[var(--body)]">We'll email you within 24 hours with available slots and a payment link.</p>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4">
                            <h2 className="font-serif text-[28px] text-[var(--ink)] mb-4">Request a consultation</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <Input label="Full name" required value={form.name} onChange={set("name")} testid="consult-name" />
                                <Input label="Phone" required value={form.phone} onChange={set("phone")} testid="consult-phone" />
                            </div>
                            <Input label="Email" type="email" required value={form.email} onChange={set("email")} testid="consult-email" />
                            <div>
                                <label className="lbl">Primary concern</label>
                                <select className="field" value={form.concern} onChange={set("concern")} data-testid="consult-concern">
                                    {SPECIALTIES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="lbl">A bit more about your situation (optional)</label>
                                <textarea className="field" rows={4} value={form.note} onChange={set("note")} />
                            </div>
                            <button className="btn btn-primary w-full" disabled={submitting} data-testid="consult-submit">{submitting ? "Sending…" : "Request Appointment"}</button>
                            {err && <div className="text-[var(--terra)] text-sm" data-testid="consult-error">{err}</div>}
                            <p className="text-[11px] text-[var(--body)] text-center">
                                Your details are encrypted and shared only with the consulting physician.
                            </p>
                        </form>
                    )}
                </div>
            </div>

            {docs.length > 0 && (() => {
                const grouped = {};
                docs.forEach((d) => {
                    const sec = d.section || "Medical Advisory Board";
                    (grouped[sec] = grouped[sec] || []).push(d);
                });
                const sections = [
                    ...DOCTOR_SECTION_ORDER.filter((s) => grouped[s]?.length),
                    ...Object.keys(grouped).filter((s) => !DOCTOR_SECTION_ORDER.includes(s)),
                ];
                return (
                    <div className="mt-20" data-testid="consult-doctors-section">
                        <div className="text-center mb-12">
                            <p className="eyebrow">Board-reviewed specialists</p>
                            <h2 className="font-serif text-[32px] sm:text-[42px] text-[var(--ink)] mt-3">Meet the Doctors</h2>
                            <p className="text-[var(--body)] max-w-xl mx-auto mt-3 text-[14px]">
                                The practitioners you may be paired with for your consultation.
                            </p>
                        </div>
                        {sections.map((sec) => (
                            <section key={sec} className="mb-14" data-testid={`consult-section-${sec.toLowerCase().replace(/\s+/g, "-")}`}>
                                <div className="flex items-center gap-4 mb-7">
                                    <h3 className="font-serif text-[22px] sm:text-[26px] text-[var(--ink)] whitespace-nowrap">{sec}</h3>
                                    <span className="flex-1 h-px bg-[var(--line)]" />
                                    <span className="text-[11px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">{grouped[sec].length} member{grouped[sec].length > 1 ? "s" : ""}</span>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                                    {grouped[sec].map((m) => <MemberCard key={m.id} m={m} />)}
                                </div>
                            </section>
                        ))}
                    </div>
                );
            })()}
        </div>
    );
}

function Input({ label, type = "text", required, value, onChange, testid }) {
    return (
        <div>
            <label className="lbl">{label}{required && " *"}</label>
            <input className="field" type={type} required={required} value={value} onChange={onChange} data-testid={testid} />
        </div>
    );
}
